import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Package, Plus, Server, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';

interface Instance {
  id: string;
  name: string;
  odoo_url: string;
  status: 'active' | 'inactive' | 'error';
  health_status: 'healthy' | 'degraded' | 'down';
  active_bobs: number;
  tier: string;
  created_at: string;
}

export function ClientDashboard() {
  const navigate = useNavigate();

  // Connect to real API
  const { data, isLoading, error } = useQuery({
    queryKey: ['my-instances'],
    queryFn: async () => {
      try {
        return await api.getMyInstances();
      } catch (err) {
        console.error('Error fetching instances:', err);
        throw err;
      }
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-slate-400">Loading your instances...</div>
      </div>
    );
  }

  if (error) {
    const errorMessage = (error as { message?: string })?.message || 'Unknown error';
    const errorStatus = (error as { status?: number })?.status;

    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <div className="text-red-400">Error loading instances</div>
        <div className="text-sm text-slate-500">
          {errorStatus === 401
            ? 'Please log in to view your instances'
            : errorStatus === 403
            ? 'You do not have permission to view instances'
            : errorMessage}
        </div>
        {errorStatus === 401 && (
          <button
            onClick={() => navigate('/login')}
            className="px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-medium transition-colors"
          >
            Go to Login
          </button>
        )}
      </div>
    );
  }

  const instances = data?.instances || [];

  const getHealthIcon = (status: Instance['health_status']) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle2 className="h-5 w-5 text-emerald-400" />;
      case 'degraded':
        return <AlertCircle className="h-5 w-5 text-yellow-400" />;
      case 'down':
        return <XCircle className="h-5 w-5 text-red-400" />;
    }
  };

  const getHealthLabel = (status: Instance['health_status']) => {
    switch (status) {
      case 'healthy':
        return 'Healthy';
      case 'degraded':
        return 'Degraded';
      case 'down':
        return 'Down';
    }
  };

  const getHealthColor = (status: Instance['health_status']) => {
    switch (status) {
      case 'healthy':
        return 'text-emerald-400';
      case 'degraded':
        return 'text-yellow-400';
      case 'down':
        return 'text-red-400';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-100">My Instances</h1>
          <p className="mt-1 text-sm text-slate-400">
            Manage your Odoo instances and BoBs
          </p>
        </div>
        <button
          onClick={() => navigate('/client/instances/create')}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-medium transition-colors"
        >
          <Plus className="h-5 w-5" />
          Create New Instance
        </button>
      </div>

      {/* Quick Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-400">Total Instances</p>
              <p className="text-3xl font-bold text-slate-100 mt-1">{instances.length}</p>
            </div>
            <Server className="h-12 w-12 text-blue-400/20" />
          </div>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-400">Active BoBs</p>
              <p className="text-3xl font-bold text-slate-100 mt-1">
                {instances.reduce((sum, inst) => sum + inst.active_bobs, 0)}
              </p>
            </div>
            <Package className="h-12 w-12 text-emerald-400/20" />
          </div>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-400">Healthy Instances</p>
              <p className="text-3xl font-bold text-slate-100 mt-1">
                {instances.filter(inst => inst.health_status === 'healthy').length}
              </p>
            </div>
            <CheckCircle2 className="h-12 w-12 text-emerald-400/20" />
          </div>
        </div>
      </div>

      {/* Empty State */}
      {instances.length === 0 && (
        <div className="text-center py-12 px-4 bg-slate-800/50 rounded-lg border border-slate-700">
          <Package className="mx-auto h-12 w-12 text-slate-600" />
          <h3 className="mt-2 text-sm font-semibold text-slate-300">No instances yet</h3>
          <p className="mt-1 text-sm text-slate-500">
            Get started by creating your first Odoo instance
          </p>
          <button
            onClick={() => navigate('/client/instances/create')}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-medium transition-colors"
          >
            <Plus className="h-5 w-5" />
            Create Instance
          </button>
        </div>
      )}

      {/* Instance Grid */}
      {instances.length > 0 && (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {instances.map((instance) => (
            <div
              key={instance.id}
              className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-6 hover:border-blue-500/50 transition-colors cursor-pointer"
              onClick={() => navigate(`/client/instances/${instance.id}`)}
            >
              {/* Instance Header */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Package className="h-5 w-5 text-blue-400" />
                    <h3 className="text-lg font-semibold text-slate-100 truncate">
                      {instance.name}
                    </h3>
                  </div>
                  <p className="mt-1 text-sm text-slate-400 truncate">{instance.odoo_url}</p>
                </div>
                <div>{getHealthIcon(instance.health_status)}</div>
              </div>

              {/* Instance Stats */}
              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Status</span>
                  <span className={getHealthColor(instance.health_status)}>
                    {getHealthLabel(instance.health_status)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Active BoBs</span>
                  <span className="text-slate-300 font-medium">{instance.active_bobs}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Tier</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 capitalize">
                    {instance.tier}
                  </span>
                </div>
              </div>

              {/* Footer */}
              <div className="mt-4 pt-4 border-t border-slate-700">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/client/instances/${instance.id}`);
                  }}
                  className="text-sm text-blue-400 hover:text-blue-300 font-medium transition-colors"
                >
                  Manage Instance →
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
